import React from 'react';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import ListSubheader from '@mui/material/ListSubheader';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import clsx from 'clsx'

interface GroupedOption<T> {
  group: string; // Group name
  options: T[];  // Options within the group
}

interface SearchableSelectProps<T> {
  options: GroupedOption<T>[]; // Grouped options
  getOptionLabel: (opt: T) => string;
  getOptionKey: (opt: T) => string;
  onChange: (opts: T[]) => void; // Return selected options
  defaultValue?: T[];
  label: string;
  placeholder: string;
  expanded: boolean;
}

export function GroupingMultiselectFilter<T>({
  options,
  getOptionLabel,
  getOptionKey,
  onChange,
  defaultValue = [],
  label,
  placeholder,
  expanded,
}: SearchableSelectProps<T>) {
  const [selectedValues, setSelectedValues] = React.useState<T[]>(defaultValue);

  return (
    <FormControl sx={{ m: 1, minWidth: 180, marginLeft: 0, display: !expanded ? 'none' : '' }}>
      {/* <InputLabel htmlFor="grouped-select">
        {label}
      </InputLabel> */}
      <Select
        multiple
        value={selectedValues.map(getOptionKey)}
        onChange={(event) => {
          console.log("Select value before handleChange:", event.target.value);
        }}
        sx={{
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#fff",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#fff", // Focus border color
            boxShadow: "none", // Remove focus shadow
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#fff", // Hover border color
            boxShadow: "none",
          },
          "& .MuiSelect-select": {
            backgroundColor: "#fff",
            color: '#003366',
            fontWeight: 600,
            "&:focus": {
              outline: "none", // Ensure no default outline
              boxShadow: "none",
              // borderColor: "#0b6d5e",
            },
          }
        }}
        renderValue={selected => {
          if (selected.length === 0) {
            // Show "Select brand" text inside input when nothing selected
            return <span style={{ color: '#888', fontSize: '1rem', fontWeight: '500' }}>Select brand...</span>;
          }
          const selectedLabels = options
            .flatMap(group => group.options)
            .filter(opt => selected.includes(getOptionKey(opt)))
            .map(opt => getOptionLabel(opt));
          if (selectedLabels.length >= 2) {
            return `${selectedLabels.length} selected`;
          }
          return selectedLabels.join(", ");
        }}
        id="grouped-select"
        label={label}
        displayEmpty
      >
        <MenuItem disabled value="" sx={{ cursor: 'default' }}>
          <em>{placeholder}</em>
        </MenuItem>
        {options.map(group => (
          <React.Fragment key={group.group}>
            <ListSubheader sx={{ cursor: 'default' }}>{group.group}</ListSubheader>
            {group.options.map(option => (
              <MenuItem key={getOptionKey(option)} value={getOptionKey(option)} sx={{ cursor: 'default' }}>
                <Checkbox
                  sx={{ cursor: 'pointer' }}  // Apply pointer cursor specifically to checkbox
                  checked={selectedValues.some(opt => getOptionKey(opt) === getOptionKey(option))}
                  onChange={(event) => {
                    const selectedKey = getOptionKey(option);
                    const isChecked = event.target.checked;
                    let newSelection: T[];

                    // Get all the options in a flat list
                    const allOptions = options.flatMap(group => group.options);

                    // If the checkbox is checked, add the option; if unchecked, remove it
                    if (isChecked) {
                      newSelection = [...selectedValues, option];
                    } else {
                      newSelection = selectedValues.filter(opt => getOptionKey(opt) !== selectedKey);
                    }

                    // Find selected options based on the updated selection
                    const selectedOptions = allOptions.filter(opt => newSelection.includes(opt));
                    if (selectedOptions.length === 0) {
                      return;
                    }
                    // Determine the groups of selected options
                    const selectedGroups = Array.from(
                      new Set(
                        selectedOptions.map(opt =>
                          options.find(group => group.options.some(o => getOptionKey(o) === getOptionKey(opt)))?.group
                        )
                      )
                    );

                    let finalSelection = selectedOptions;

                    // If multiple groups are selected, retain only the current group
                    if (selectedGroups.length > 1) {
                      const lastSelectedKey = getOptionKey(newSelection[newSelection.length - 1]);
                      const lastSelectedOption = allOptions.find(opt => getOptionKey(opt) === lastSelectedKey);

                      const lastSelectedGroup = options.find(group =>
                        group.options.some(opt => getOptionKey(opt) === getOptionKey(lastSelectedOption!))
                      );

                      if (lastSelectedGroup) {
                        finalSelection = lastSelectedGroup.options.filter(opt =>
                          newSelection.includes(opt)
                        );
                        console.log("Final selection (single group retained):", finalSelection);
                      }
                    }

                    // Update the state with the final selected values
                    setSelectedValues(finalSelection);
                    console.log("Updated selected values:", finalSelection);

                    // Pass the final selection to the parent component
                    onChange(finalSelection);
                  }}
                />
                <ListItemText primary={getOptionLabel(option)} sx={{ cursor: 'default' }} />
              </MenuItem>
            ))}
          </React.Fragment>
        ))}
      </Select>
    </FormControl>
  );
}
